import { useEffect, useMemo, useRef, useState } from 'react';
import { LiveProcessingQuestion, useListLiveProcessingQuery } from '@/api';
import { LIVE_PROCESSING_POLL_INTERVAL_MS, LIVE_SETTLED_WINDOW_MARGIN_SECONDS } from './constants';
import { readLiveProcessingQueue, writeLiveProcessingQueue } from './storage';

interface LiveProcessingQueue {
  /** Questions the worker is genuinely still working on. */
  processingCount: number;
  /** Questions the backend has given up on; they never leave the list. */
  stalledQuestions: LiveProcessingQuestion[];
}

/**
 * Watches the live-question queue for a case and calls `onQueueDrained` when a
 * question leaves it, which is the panel's cue to pull the answers again. It's
 * told how many seconds back to look for the questions that have just left: to
 * the last poll that still saw them queued, plus some slack.
 *
 * Questions are followed by id rather than counted, so one being answered while
 * another is sent still reads as a question leaving.
 *
 * Polling stops once the queue empties — an empty queue can only refill from a
 * send, and that invalidates the tag, so there is nothing to poll for in
 * between. A load that failed keeps polling, as the queue then reads as empty
 * whatever is really in it. The queue as last seen is kept in localStorage so a
 * reload mid-answer still reads the question leaving as "an answer landed"
 * rather than as a fresh baseline.
 */
export function useLiveProcessingQueue(
  caseId: string | undefined,
  onQueueDrained: (lookBackSeconds: number) => Promise<void>,
): LiveProcessingQueue {
  const onQueueDrainedRef = useRef(onQueueDrained);
  onQueueDrainedRef.current = onQueueDrained;

  const [shouldPoll, setShouldPoll] = useState(false);
  const queue = useListLiveProcessingQuery(
    { caseId: caseId ?? '', isNormalised: false },
    {
      skip: !caseId,
      pollingInterval: shouldPoll ? LIVE_PROCESSING_POLL_INTERVAL_MS : 0,
      skipPollingIfUnfocused: true,
      // Polling is held back while the tab is hidden, so coming back to it
      // checks straight away rather than on the next tick.
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    },
  );

  // A stalled question sits in the list indefinitely, so counting it would keep
  // the bar spinning and the polling running for as long as the panel is open.
  // Memoised because the caller reports these to the user question by question,
  // and a fresh array on every render would restate them on every render too.
  const queuedQuestions = queue.currentData?.questions;
  const stalledQuestions = useMemo(
    () => queuedQuestions?.filter((question) => question.isStaleInProcessing) ?? [],
    [queuedQuestions],
  );
  const processingCount = (queuedQuestions?.length ?? 0) - stalledQuestions.length;
  // Stalled questions are followed too: one leaving at last has settled like
  // any other. Stable across polls that bring back the same queue, as RTK Query
  // keeps the previous response when the new one matches it.
  const queuedQuestionIds = useMemo(
    () => queuedQuestions?.map(({ questionId }) => questionId) ?? [],
    [queuedQuestions],
  );
  const hasLoaded = Boolean(queue.currentData);
  const isError = queue.isError;
  const lastPolledAt = queue.fulfilledTimeStamp;

  useEffect(() => {
    setShouldPoll(processingCount > 0 || isError);
  }, [isError, processingCount]);

  // A change in what's queued: something left, joined, or both.
  useEffect(() => {
    if (!caseId || !hasLoaded || isError) return;

    const stored = readLiveProcessingQueue(caseId);
    const left = stored?.questionIds.filter((questionId) => !queuedQuestionIds.includes(questionId)) ?? [];

    // Only joining can't mean an answer arrived, and no baseline means this is
    // the first look at the case — the panel has already fetched on mount.
    if (!stored || left.length === 0) {
      writeLiveProcessingQueue(caseId, queuedQuestionIds);
      return;
    }

    const secondsSinceLastSeen = Math.max(0, Math.ceil((Date.now() - stored.recordedAt) / 1000));
    const lookBackSeconds = secondsSinceLastSeen + LIVE_SETTLED_WINDOW_MARGIN_SECONDS;

    let cancelled = false;
    onQueueDrainedRef
      .current(lookBackSeconds)
      .then(() => {
        // Held back until the answers are in, so a failed refresh is retried on
        // the next poll or remount rather than being recorded as handled.
        if (!cancelled) writeLiveProcessingQueue(caseId, queuedQuestionIds);
      })
      .catch(() => {
        // Already surfaced by the caller; swallowed here to keep the rejection
        // from escaping the effect.
      });

    return () => {
      cancelled = true;
    };
  }, [caseId, hasLoaded, isError, queuedQuestionIds]);

  // A poll that finds the same questions proves none of them had left by then,
  // so the record moves up to it. That keeps the look-back to the gap since the
  // last poll rather than since the queue last changed — which can be days, and
  // would bring back answers the user saw long ago. Anything else is the effect
  // above's to record, and it can be mid-refresh, so this leaves it alone.
  useEffect(() => {
    if (!caseId || !lastPolledAt || isError) return;

    const stored = readLiveProcessingQueue(caseId);
    if (stored?.questionIds.join(',') === queuedQuestionIds.join(',')) {
      writeLiveProcessingQueue(caseId, queuedQuestionIds);
    }
  }, [caseId, isError, lastPolledAt, queuedQuestionIds]);

  return { processingCount, stalledQuestions };
}
